import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, LogIn, Mail, Lock, ArrowRight, RefreshCcw, ArrowLeft, Info } from 'lucide-react';
import client, { apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

const ROLE_CONFIG = {
  SUPER_ADMIN: {
    endpoint: '/auth/super-admin/login',
    title: 'Super Admin',
    subtitle: 'Platform control center',
    dashboardPath: '/admin',
    loginPath: '/admin/login',
    otherRoleName: 'Warehouse Admin',
    otherRolePath: '/warehouse/login',
  },
  WAREHOUSE_ADMIN: {
    endpoint: '/auth/warehouse/login',
    title: 'Warehouse Admin',
    subtitle: 'Manage your warehouse',
    dashboardPath: '/warehouse',
    loginPath: '/warehouse/login',
    otherRoleName: 'Super Admin',
    otherRolePath: '/admin/login',
  },
};

export default function LoginPage({ role }) {
  const cfg = ROLE_CONFIG[role];
  const navigate = useNavigate();
  const { login } = useAuth();

  // Step: 'credentials' | 'otp' | 'forgot'
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // { challengeId, expiresAt, resendAvailableAt, otp?, ... }
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);

  async function submitCredentials(e) {
  e.preventDefault();
  setLoading(true);

  try {
    const res = await client.post(cfg.endpoint, {
      email: email.trim().toLowerCase(),
      password
    });

    setChallenge(res.data.data);
    setStep('otp');
    setAttemptsLeft(res.data.data?.maxAttempts ?? null);

    toast.success('OTP sent to your email');

    if (res.data.data?.otp) {
      toast.info(`Dev mode OTP: ${res.data.data.otp}`, {
        duration: 10000
      });
    }
  } catch (err) {
    toast.error(apiErrorMessage(err, 'Login failed'));
  } finally {
    setLoading(false);
  }
}

  async function submitOtp(codeOverride) {
    const code = typeof codeOverride === 'string' ? codeOverride : otp;
    if (!code || code.length !== 6) {
      setOtpError('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    setOtpError('');
    try {
      const res = await client.post('/auth/verify-otp', {
        challengeId: challenge.challengeId,
        otp: code,
      });
      const { accessToken, refreshToken, user } = res.data.data;
      login({ accessToken, refreshToken, user });
      toast.success('Welcome back');
      navigate(cfg.dashboardPath, { replace: true });
    } catch (err) {
      const msg = apiErrorMessage(err, 'Verification failed');
      const left = err?.response?.data?.error?.details?.attemptsLeft;
      if (left !== undefined) setAttemptsLeft(left);
      setOtpError(msg);
      setOtp('');
    } finally {
      setLoading(false);
    }
  }

  // Auto-submit when 6 digits are entered — driven by state, so no race.
  useEffect(() => {
    if (step === 'otp' && otp.length === 6 && !loading && !otpError) {
      submitOtp(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step]);

  async function handleOtpFormSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    await submitOtp();
  }

  async function resend() {
  setLoading(true);

  try {
    const res = await client.post('/auth/resend-otp', {
      challengeId: challenge.challengeId
    });

    setChallenge(res.data.data);
    setAttemptsLeft(res.data.data?.maxAttempts ?? null);
    setOtp('');
    setOtpError('');

    toast.success('New OTP sent');

    if (res.data.data?.otp) {
      toast.info(`Dev mode OTP: ${res.data.data.otp}`, {
        duration: 10000
      });
    }
  } catch (err) {
    toast.error(apiErrorMessage(err, 'Could not resend OTP'));
  } finally {
    setLoading(false);
  }
}

  return (
    <div className="min-h-screen w-full bg-white flex" data-testid="login-page-root">
      {/* Left form */}
      <div className="w-full lg:w-[46%] xl:w-[42%] flex flex-col p-8 md:p-14">
        <div className="flex items-center gap-2 mb-16">
          <div className="w-9 h-9 rounded-md bg-zinc-950 text-white grid place-items-center font-display font-bold">S</div>
          <div>
            <div className="font-display text-lg font-bold leading-none">Stockanize</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Warehouse OS</div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm">
            <React.Fragment>
              {step === 'credentials' && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">{cfg.subtitle}</div>
                  <h1 className="font-display text-4xl font-bold text-zinc-950 mb-2 leading-tight">
                    Sign in to<br />Stockanize.
                  </h1>
                  <p className="text-sm text-zinc-500 mb-8">
                    {cfg.title} portal · Two-step verification enabled.
                  </p>
                  <form onSubmit={submitCredentials} className="space-y-5" data-testid="login-form">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-600">Email</Label>
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-3 text-zinc-400" strokeWidth={1.75} />
                        <Input
                          id="email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-9 h-11 border-zinc-200 focus-visible:ring-zinc-950"
                          placeholder="you@company.com"
                          data-testid="login-email-input"
                          autoComplete="email"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-600">Password</Label>
                        <button
                          type="button"
                          onClick={() => setStep('forgot')}
                          className="text-xs text-zinc-500 hover:text-zinc-950 transition-colors"
                          data-testid="login-forgot-password-link"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="w-4 h-4 absolute left-3 top-3 text-zinc-400" strokeWidth={1.75} />
                        <Input
                          id="password"
                          type="password"
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-9 h-11 border-zinc-200 focus-visible:ring-zinc-950"
                          placeholder="Enter your password"
                          data-testid="login-password-input"
                          autoComplete="current-password"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white font-medium"
                      data-testid="login-submit-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                    </Button>
                    <div className="text-center text-xs text-zinc-500 pt-2">
                      Not a {cfg.title}?{' '}
                      <Link to={cfg.otherRolePath} className="text-zinc-950 font-medium hover:underline" data-testid="login-switch-role-link">
                        {cfg.otherRoleName} sign in
                      </Link>
                    </div>
                  </form>
                </div>
              )}

              {step === 'otp' && challenge && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Two-step verification</div>
                  <h1 className="font-display text-4xl font-bold text-zinc-950 mb-2 leading-tight">
                    Enter the<br />6-digit code.
                  </h1>
                  <p className="text-sm text-zinc-500 mb-8">
                    Sent to <span className="font-mono text-zinc-900">{challenge.email}</span>. Expires in{' '}
                    <ExpiresCountdown target={challenge.expiresAt} /> min.
                  </p>

                  <form onSubmit={handleOtpFormSubmit} className="space-y-6" data-testid="otp-form">
                    <div className="flex flex-col items-center gap-3">
                      <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={(v) => { setOtp(v); setOtpError(''); }}
                        data-testid="otp-input"
                      >
                        <InputOTPGroup>
                          {[0,1,2,3,4,5].map((i) => (
                            <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg font-mono border-zinc-200" />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                      {otpError && (
                        <div className="text-sm text-red-600 font-medium" data-testid="otp-error">{otpError}</div>
                      )}
                      {attemptsLeft !== null && attemptsLeft > 0 && !otpError && (
                        <div className="text-xs text-zinc-500 font-mono">{attemptsLeft} attempt(s) remaining</div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white font-medium"
                      data-testid="otp-verify-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify & sign in <LogIn className="w-4 h-4 ml-1.5" /></>}
                    </Button>

                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => { setStep('credentials'); setOtp(''); setOtpError(''); }}
                        className="flex items-center gap-1 text-zinc-500 hover:text-zinc-950 transition-colors"
                        data-testid="otp-back-button"
                      >
                        <ArrowLeft className="w-3 h-3" /> Use different account
                      </button>
                      <ResendButton
                        target={challenge.resendAvailableAt}
                        onClick={resend}
                        loading={loading}
                      />
                    </div>

                    {challenge.otp && (
                      <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-100 text-xs text-blue-900" data-testid="otp-dev-hint">
                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                        <div><span className="font-semibold">Dev mode:</span> OTP is <span className="font-mono font-bold">{challenge.otp}</span>. In production, this hint is hidden and the code is delivered only via email.</div>
                      </div>
                    )}
                  </form>
                </div>
              )}

              {step === 'forgot' && (
                <ForgotPasswordBlock role={role} onBack={() => setStep('credentials')} />
              )}
            </React.Fragment>
          </div>
        </div>

        <div className="text-xs text-zinc-400 mt-8">
          © {new Date().getFullYear()} Stockanize. Warehouse Management System.
        </div>
      </div>

      {/* Right art */}
      <div className="hidden lg:block relative flex-1 bg-zinc-950 grid-pattern overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/40 to-transparent" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2 opacity-70">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono">SYSTEM ONLINE</div>
          </div>

          <div className="max-w-md">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-zinc-400 mb-3">// Purpose-built for warehouses</div>
            <h2 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05] mb-6">
              Precision inventory, <span className="text-emerald-400">precise billing.</span>
            </h2>
            <p className="text-zinc-300 text-lg leading-relaxed max-w-sm">
              Approve dispatches, generate GST-compliant invoices, and reconcile per-carton-per-day charges — all in one operator dashboard.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8 font-mono text-xs pt-6 border-t border-white/10">
            <div>
              <div className="text-2xl font-display font-bold text-white">99.9%</div>
              <div className="text-zinc-400 mt-1">API uptime</div>
            </div>
            <div>
              <div className="text-2xl font-display font-bold text-white">2FA</div>
              <div className="text-zinc-400 mt-1">Email OTP</div>
            </div>
            <div>
              <div className="text-2xl font-display font-bold text-white">GST</div>
              <div className="text-zinc-400 mt-1">Auto-invoice</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------- countdown helpers --------
function ExpiresCountdown({ target }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((new Date(target) - new Date()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => setRemaining(Math.max(0, Math.floor((new Date(target) - new Date()) / 1000))), 1000);
    return () => clearInterval(t);
  }, [target]);
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return <span className="font-mono">{m}:{String(s).padStart(2, '0')}</span>;
}

function ResendButton({ target, onClick, loading }) {
  const [wait, setWait] = useState(() => Math.max(0, Math.floor((new Date(target) - new Date()) / 1000)));
  useEffect(() => {
    setWait(Math.max(0, Math.floor((new Date(target) - new Date()) / 1000)));
    const t = setInterval(() => setWait((w) => Math.max(0, w - 1)), 1000);
    return () => clearInterval(t);
  }, [target]);
  const canResend = wait <= 0;
  return (
    <button
      type="button"
      disabled={!canResend || loading}
      onClick={onClick}
      className={`flex items-center gap-1 transition-colors ${canResend ? 'text-zinc-950 hover:opacity-70' : 'text-zinc-400 cursor-not-allowed'}`}
      data-testid="otp-resend-button"
    >
      <RefreshCcw className="w-3 h-3" />
      {canResend ? 'Resend code' : `Resend in ${wait}s`}
    </button>
  );
}

// -------- forgot / reset --------
function ForgotPasswordBlock({ role, onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState('');

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await client.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
      if (res?.data?.data?.resetUrl) setDevUrl(res.data.data.resetUrl);
      toast.success('If the account exists, a reset link has been sent.');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-950 mb-6" data-testid="forgot-back-button">
        <ArrowLeft className="w-3 h-3" /> Back to sign in
      </button>
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Account recovery</div>
      <h1 className="font-display text-4xl font-bold text-zinc-950 mb-2 leading-tight">Reset your<br />password.</h1>
      <p className="text-sm text-zinc-500 mb-8">
        Enter your email and we&apos;ll send you a secure reset link.
      </p>
      {sent ? (
        <div className="p-4 border border-zinc-200 rounded-md bg-zinc-50 space-y-2" data-testid="forgot-sent-block">
          <div className="text-sm text-zinc-950 font-medium">Check your inbox.</div>
          <div className="text-xs text-zinc-500">If <span className="font-mono">{email}</span> is registered, you&apos;ll receive a reset link shortly.</div>
          {devUrl && (
            <div className="mt-3 pt-3 border-t border-zinc-200 text-xs">
              <div className="uppercase tracking-widest text-zinc-500 mb-1">Dev mode</div>
              <a href={devUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all font-mono" data-testid="forgot-dev-reset-link">{devUrl}</a>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <div className="space-y-2">
            <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-zinc-600">Email</Label>
            <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11 border-zinc-200 focus-visible:ring-zinc-950" data-testid="forgot-email-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white font-medium" data-testid="forgot-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
          </Button>
        </form>
      )}
    </div>
  );
}
